import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, X, CheckCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

const API = process.env.REACT_APP_BACKEND_URL;

const emptyProperty = {
  title: '',
  property_type: '',
  transaction_type: '',
  location: '',
  zip_code: '',
  price: '',
  price_on_request: false,
  area_sqm: '',
  rooms: '',
  floor: '',
  description: '',
  features: [],
  images: [],
  status: 'Verfügbar'
};

export default function AdminProperties() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [currentProperty, setCurrentProperty] = useState(null);
  const [formData, setFormData] = useState(emptyProperty);
  const [featuresInput, setFeaturesInput] = useState('');
  const [imagesInput, setImagesInput] = useState('');
  const [soldDialogOpen, setSoldDialogOpen] = useState(false);
  const [soldData, setSoldData] = useState({ status: 'Verkauft', days_on_market: '', testimonial: '', testimonial_author: '' });

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API}/api/properties`);
      setProperties(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Immobilien');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setCurrentProperty(null);
    setFormData(emptyProperty);
    setFeaturesInput('');
    setImagesInput('');
    setDialogOpen(true);
  };

  const handleEdit = (property) => {
    setCurrentProperty(property);
    setFormData({
      ...property,
      price: property.price || '',
      rooms: property.rooms || '',
      floor: property.floor || ''
    });
    setFeaturesInput(property.features?.join(', ') || '');
    setImagesInput(property.images?.join('\n') || '');
    setDialogOpen(true);
  };

  const handleDelete = (property) => {
    setCurrentProperty(property);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/properties/${currentProperty.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Immobilie gelöscht');
      fetchProperties();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
    setDeleteDialogOpen(false);
  };
  
  const handleMarkAsSold = (property) => {
  setCurrentProperty(property);
  setSoldData({ status: 'Verkauft', days_on_market: '', testimonial: '', testimonial_author: '' });
  setSoldDialogOpen(true);
};

const confirmMarkAsSold = async () => {
  try {
    const token = localStorage.getItem('token');
    await axios.post(
      `${API}/api/properties/${currentProperty.id}/mark-as-sold`,
      {
        status: soldData.status,
        days_on_market: soldData.days_on_market ? parseInt(soldData.days_on_market) : null,
        testimonial: soldData.testimonial || null,
        testimonial_author: soldData.testimonial_author || null,
      },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    toast.success(`Immobilie als ${soldData.status} markiert und zu Referenzen verschoben`);
    setSoldDialogOpen(false);
    fetchProperties();
  } catch (error) {
    toast.error('Fehler beim Markieren');
  }
};
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    
    const data = {
      ...formData,
      price: formData.price_on_request ? null : parseFloat(formData.price) || null,
      area_sqm: parseFloat(formData.area_sqm),
      rooms: formData.rooms ? parseFloat(formData.rooms) : null,
      features: featuresInput.split(',').map(f => f.trim()).filter(Boolean),
      images: imagesInput.split('\n').map(i => i.trim()).filter(Boolean)
    };

    try {
      if (currentProperty) {
        await axios.put(`${API}/api/properties/${currentProperty.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Immobilie aktualisiert');
      } else {
        await axios.post(`${API}/api/properties`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Immobilie erstellt');
      }
      setDialogOpen(false);
      fetchProperties();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  return (
    <div className="p-8" data-testid="admin-properties">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-3xl text-white mb-2">Immobilien</h1>
          <p className="text-[#c8c6c4]">{properties.length} Einträge</p>
        </div>
        <button onClick={handleCreate} className="btn-primary flex items-center gap-2" data-testid="add-property-btn">
          <Plus className="w-4 h-4" />
          Neue Immobilie
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="spinner" />
        </div>
      ) : properties.length === 0 ? (
        <div className="text-center py-20">
          <p className="text-[#c8c6c4]">Noch keine Immobilien vorhanden.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#d1af6a]/20">
                <th className="text-left py-4 px-4 text-[#d1af6a] font-accent text-xs tracking-wider">Titel</th>
                <th className="text-left py-4 px-4 text-[#d1af6a] font-accent text-xs tracking-wider">Typ</th>
                <th className="text-left py-4 px-4 text-[#d1af6a] font-accent text-xs tracking-wider">Ort</th>
                <th className="text-left py-4 px-4 text-[#d1af6a] font-accent text-xs tracking-wider">Preis</th>
                <th className="text-left py-4 px-4 text-[#d1af6a] font-accent text-xs tracking-wider">Status</th>
                <th className="text-right py-4 px-4 text-[#d1af6a] font-accent text-xs tracking-wider">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {properties.map((property) => (
                <tr key={property.id} className="border-b border-[#d1af6a]/10 hover:bg-[#1a2640]">
                  <td className="py-4 px-4 text-white">{property.title}</td>
                  <td className="py-4 px-4 text-[#c8c6c4]">{property.property_type}</td>
                  <td className="py-4 px-4 text-[#c8c6c4]">{property.location}</td>
                  <td className="py-4 px-4 text-[#c8c6c4]">
                    {property.price_on_request ? 'Auf Anfrage' : property.price ? `€${property.price.toLocaleString()}` : '-'}
                  </td>
                  <td className="py-4 px-4">
                    <span className={`px-2 py-1 text-xs ${
                      property.status === 'Neu' ? 'badge-neu' : 
                      property.status === 'Verfügbar' ? 'badge-verfugbar' : 
                      property.status === 'In Verhandlung' ? 'badge-verhandlung' : 'badge-verkauft'
                    }`}>
                      {property.status}
                    </span>
                  </td>
                  <td className="py-4 px-4 text-right">
                    <button onClick={() => handleEdit(property)} className="p-2 text-[#c8c6c4] hover:text-[#d1af6a]" title="Bearbeiten">
  <Pencil className="w-4 h-4" />
</button>
<button onClick={() => handleMarkAsSold(property)} className="p-2 text-[#c8c6c4] hover:text-green-400" title="Als verkauft markieren">
  <CheckCircle className="w-4 h-4" />
</button>
<button onClick={() => handleDelete(property)} className="p-2 text-[#c8c6c4] hover:text-red-400" title="Löschen">
  <Trash2 className="w-4 h-4" />
</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#1a2640] border-[#d1af6a]/20 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white font-display text-xl">
              {currentProperty ? 'Immobilie bearbeiten' : 'Neue Immobilie'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <input
              type="text"
              placeholder="Titel *"
              required
              value={formData.title}
              onChange={(e) => setFormData(p => ({ ...p, title: e.target.value }))}
              className="input-luxury w-full"
            />
            <div className="grid grid-cols-2 gap-4">
              <Select value={formData.property_type} onValueChange={(v) => setFormData(p => ({ ...p, property_type: v }))}>
                <SelectTrigger className="input-luxury"><SelectValue placeholder="Objektart *" /></SelectTrigger>
                <SelectContent className="bg-[#1a2640] border-[#d1af6a]/20">
                  <SelectItem value="Wohnung">Wohnung</SelectItem>
                  <SelectItem value="Haus">Haus</SelectItem>
                  <SelectItem value="Grundstück">Grundstück</SelectItem>
                  <SelectItem value="Gewerbe">Gewerbe</SelectItem>
                </SelectContent>
              </Select>
              <Select value={formData.transaction_type} onValueChange={(v) => setFormData(p => ({ ...p, transaction_type: v }))}>
                <SelectTrigger className="input-luxury"><SelectValue placeholder="Kaufen/Mieten *" /></SelectTrigger>
                <SelectContent className="bg-[#1a2640] border-[#d1af6a]/20">
                  <SelectItem value="Kaufen">Kaufen</SelectItem>
                  <SelectItem value="Mieten">Mieten</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <input type="text" placeholder="Ort *" required value={formData.location} onChange={(e) => setFormData(p => ({ ...p, location: e.target.value }))} className="input-luxury w-full" />
              <input type="text" placeholder="PLZ *" required value={formData.zip_code} onChange={(e) => setFormData(p => ({ ...p, zip_code: e.target.value }))} className="input-luxury w-full" />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <input type="number" placeholder="Preis (€)" value={formData.price} onChange={(e) => setFormData(p => ({ ...p, price: e.target.value }))} className="input-luxury w-full" disabled={formData.price_on_request} />
              <label className="flex items-center gap-2 text-[#c8c6c4] text-sm">
                <input type="checkbox" checked={formData.price_on_request} onChange={(e) => setFormData(p => ({ ...p, price_on_request: e.target.checked }))} className="accent-[#d1af6a]" />
                Preis auf Anfrage
              </label>
              <Select value={formData.status} onValueChange={(v) => setFormData(p => ({ ...p, status: v }))}>
                <SelectTrigger className="input-luxury"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1a2640] border-[#d1af6a]/20">
                  <SelectItem value="Neu">Neu</SelectItem>
                  <SelectItem value="Verfügbar">Verfügbar</SelectItem>
                  <SelectItem value="In Verhandlung">In Verhandlung</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <input type="number" placeholder="Fläche m² *" required value={formData.area_sqm} onChange={(e) => setFormData(p => ({ ...p, area_sqm: e.target.value }))} className="input-luxury w-full" />
              <input type="number" step="0.5" placeholder="Zimmer" value={formData.rooms} onChange={(e) => setFormData(p => ({ ...p, rooms: e.target.value }))} className="input-luxury w-full" />
              <input type="text" placeholder="Etage" value={formData.floor} onChange={(e) => setFormData(p => ({ ...p, floor: e.target.value }))} className="input-luxury w-full" />
            </div>
            <textarea placeholder="Beschreibung *" required rows={3} value={formData.description} onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))} className="input-luxury w-full resize-none" />
            <input type="text" placeholder="Features (kommagetrennt)" value={featuresInput} onChange={(e) => setFeaturesInput(e.target.value)} className="input-luxury w-full" />
{/* Cloudinary Upload */}
<div className="space-y-3">
  <label className="text-[#c8c6c4] text-sm">Bilder hochladen</label>
  <input
    type="file"
    accept="image/*"
    multiple
    onChange={async (e) => {
      const files = Array.from(e.target.files);
      const uploadedUrls = [];
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('upload_preset', 'dogan_immobilien');
        try {
          const res = await fetch(
            `https://api.cloudinary.com/v1_1/dviksywea/image/upload`,
            { method: 'POST', body: formData }
          );
          const data = await res.json();
          uploadedUrls.push(data.secure_url);
        } catch (err) {
          toast.error('Fehler beim Hochladen: ' + file.name);
        }
      }
      const existing = imagesInput ? imagesInput.split('\n').filter(Boolean) : [];
      setImagesInput([...existing, ...uploadedUrls].join('\n'));
      toast.success(`${uploadedUrls.length} Bild(er) hochgeladen`);
    }}
    className="input-luxury w-full"
  />
  {imagesInput && (
    <div className="grid grid-cols-3 gap-2">
      {imagesInput.split('\n').filter(Boolean).map((url, i) => (
        <div key={i} className="relative">
          <img src={url} alt={`Bild ${i+1}`} className="w-full h-24 object-cover rounded" />
          <button
            type="button"
            onClick={() => {
              const urls = imagesInput.split('\n').filter(Boolean);
              urls.splice(i, 1);
              setImagesInput(urls.join('\n'));
            }}
            className="absolute top-1 right-1 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
          >
            ×
          </button>
        </div>
      ))}
    </div>
  )}
</div>
    <div className="flex justify-end gap-4 pt-4">
              <button type="button" onClick={() => setDialogOpen(false)} className="btn-secondary">Abbrechen</button>
              <button type="submit" className="btn-primary">Speichern</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

{/* Mark as Sold Dialog */}
      <Dialog open={soldDialogOpen} onOpenChange={setSoldDialogOpen}>
        <DialogContent className="bg-[#1a2640] border-[#d1af6a]/20 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white font-display text-xl">
              Als verkauft/vermietet markieren
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-[#c8c6c4] text-sm">
              „{currentProperty?.title}" wird aus den Angeboten entfernt und automatisch zu den Referenzen hinzugefügt.
            </p>
            <Select value={soldData.status} onValueChange={(v) => setSoldData(p => ({ ...p, status: v }))}>
              <SelectTrigger className="input-luxury">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#1a2640] border-[#d1af6a]/20">
                <SelectItem value="Verkauft">Verkauft</SelectItem>
                <SelectItem value="Vermietet">Vermietet</SelectItem>
                <SelectItem value="Vermittelt">Vermittelt</SelectItem>
              </SelectContent>
            </Select>
            <input
              type="number"
              placeholder="Verkauft in X Tagen (optional)"
              value={soldData.days_on_market}
              onChange={(e) => setSoldData(p => ({ ...p, days_on_market: e.target.value }))}
              className="input-luxury w-full"
            />
            <textarea
              placeholder="Kunden-Testimonial (optional)"
              rows={2}
              value={soldData.testimonial}
              onChange={(e) => setSoldData(p => ({ ...p, testimonial: e.target.value }))}
              className="input-luxury w-full resize-none"
            />
            <input
              type="text"
              placeholder="Testimonial-Autor (optional, z.B. Familie M., Frankfurt)"
              value={soldData.testimonial_author}
              onChange={(e) => setSoldData(p => ({ ...p, testimonial_author: e.target.value }))}
              className="input-luxury w-full"
            />
            <div className="flex justify-end gap-4 pt-2">
              <button onClick={() => setSoldDialogOpen(false)} className="btn-secondary">
                Abbrechen
              </button>
              <button onClick={confirmMarkAsSold} className="btn-primary">
                ✓ Bestätigen
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-[#1a2640] border-[#d1af6a]/20">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Immobilie löschen?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#c8c6c4]">
              Diese Aktion kann nicht rückgängig gemacht werden. Die Immobilie "{currentProperty?.title}" wird dauerhaft gelöscht.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="btn-secondary">Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700 text-white">Löschen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
